import {$qsAll, loadScript} from '../../../../@shared/ts/dom';
import {FeatureFlag} from '../../../../@type/features';
import {type NoncePayload, type PoyntOptions} from '../../../../native-checkout/ts/poynt/types';
import {type Token} from '../../../../native-checkout/ts/poynt/class-poynt-card-payment';
import {checkRequiredFields} from '../../features/additionalFields';
import {updateCustomerDataInBrowser} from '../../features/customer';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {Feature, startModalProcessing, stopModalLoading} from '../../reducers/environmentReducer';
import {PaymentConfiguration, registerGatewayBatch} from '../../reducers/paymentConfigurationReducer';
import {PeachPayCustomer, updateCustomerPreferredGateway} from '../../reducers/peachPayCustomerReducer';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';
import {store} from '../../store';
import {displayPaymentErrorMessage, type OrderService} from '../order';
import {clearLocalSession} from '../session';
import {setupPoyntButton} from './button';
import {initPoyntCardMethod} from './card';

/**
 * Implementation type for a GoDaddy Poynt gateway
 */
export interface PoyntGateway {
	config: GatewayConfiguration;
	init?: (options: PoyntOptions) => void;
	tokenize: (payload: NoncePayload) => Promise<Token>;
}

export default async function initPoyntPaymentIntegration(orderService: OrderService) {
	if (!Feature.enabled(FeatureFlag.POYNT_GATEWAYS)) {
		return;
	}

	const poyntSrc = Feature.metadata<string>(FeatureFlag.POYNT_GATEWAYS, 'poynt_src');
	const businessId = Feature.metadata<string>(FeatureFlag.POYNT_GATEWAYS, 'business_id');
	const applicationId = Feature.metadata<string>(FeatureFlag.POYNT_GATEWAYS, 'application_id');

	if (!businessId || !applicationId || !poyntSrc) {
		return;
	}

	const poyntOptions = {businessId, applicationId};

	await loadScript(poyntSrc, null, () => {
		const gateways = registerPoyntGateways();
		activatePoyntGateways(gateways, orderService, poyntOptions);
		setupPoyntButton();
	});
}

function registerPoyntGateways(): Record<string, PoyntGateway> {
	const gatewayImplementations: Record<string, PoyntGateway> = {};
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	if (Feature.enabled(FeatureFlag.POYNT_CARD_GATEWAY)) {
		const method = initPoyntCardMethod();
		gatewayConfigurations[method.config.gatewayId] = method.config;
		gatewayImplementations[method.config.gatewayId] = method;
	}

	store.dispatch(registerGatewayBatch(gatewayConfigurations));

	return gatewayImplementations;
}

function activatePoyntGateways(gateways: Record<string, PoyntGateway>, orderService: OrderService, options: PoyntOptions) {
	for (const [_, gateway] of Object.entries(gateways)) {
		if (gateway.init) {
			gateway.init(options);
		}
	}

	const confirm = (event: MouseEvent) => {
		const requiredFieldsVerification = checkRequiredFields();
		if (!requiredFieldsVerification) {
			return;
		}

		const $target = event.target as HTMLElement | null;
		const $button = $target?.closest<HTMLButtonElement>('button');
		if (!$button) {
			return;
		}

		const gatewayId = PaymentConfiguration.selectedGateway();
		const gateway = gateways[gatewayId];
		if (!gateway) {
			return;
		}

		return poyntPaymentFlow(gateway, orderService);
	};

	$qsAll<HTMLElement>('.poynt-btn', $el => {
		$el.addEventListener('click', event => confirm(event));
	});
}

async function poyntPaymentFlow(payment: PoyntGateway, orderService: OrderService) {
	$qsAll('.pp-pm-error-text', $el => {
		$el.remove();
	});

	store.dispatch(startModalProcessing());

	const payload = getNoncePayload();

	const {nonce, error} = await payment.tokenize(payload);

	const transaction = await orderService.startTransaction(payment.config.gatewayId);
	if (!transaction) {
		store.dispatch(stopModalLoading());
		return;
	}

	if (error ?? !nonce) {
		await transaction.complete({
			paymentStatus: 'creationfailed',
			orderStatus: 'unperformed',
			note: error ?? 'An unknown error occured while retrieving the nonce.',
		});

		displayPaymentErrorMessage(error ?? 'An unknown error occured while tokenizing the payment method.');
		store.dispatch(stopModalLoading());
		return;
	}

	const order = await orderService.placeOrder(transaction, {
		peachpay_session_id: PeachPayOrder.sessionId(),
		peachpay_transaction_id: transaction.getId(),
		peachpay_poynt_nonce: nonce,
	});

	if (order.result === 'failure') {
		await transaction?.complete();
		store.dispatch(stopModalLoading());
		return;
	}

	store.dispatch(updateCustomerPreferredGateway(PaymentConfiguration.selectedGateway()));

	updateCustomerDataInBrowser();
	clearLocalSession();

	const successURL = orderService.getOrderRedirect(order);
	if (window.top) {
		await transaction.complete();
		window.top.location = successURL;
	}
}

function getNoncePayload(): NoncePayload {
	const payload = {} as NoncePayload;
	if (PeachPayCustomer.billing.email()) {
		payload.emailAddress = PeachPayCustomer.billing.email();
	}

	if (PeachPayCustomer.billing.firstName()) {
		payload.firstName = PeachPayCustomer.billing.firstName();
	}

	if (PeachPayCustomer.billing.lastName()) {
		payload.lastName = PeachPayCustomer.billing.lastName();
	}

	if (PeachPayCustomer.billing.address1()) {
		payload.line1 = PeachPayCustomer.billing.address1();
	}

	if (PeachPayCustomer.billing.postal()) {
		payload.zipCode = PeachPayCustomer.billing.postal();
	}

	return payload;
}
